/**
 * ExtractionReview — per-field verification UI for OCR-extracted values.
 *
 * Each field returned by the upload action gets its own card. The user can:
 *   • Accept  — confirms the extracted value as-is
 *   • Edit    — opens an inline editor using the correct response component
 *               for the field type (TextResponse, SelectResponse, etc.)
 *   • Remove  — discards the extracted value (field returns to pending in chat)
 *
 * The "Continue" button enables when every field is in accepted, saved, or
 * removed status (none left in pending or editing).
 *
 * Calls onConfirm({ confirmed: Record<string,unknown>, rejected: string[] })
 * once the user is satisfied. The UploadScreen owns the actual API call.
 */

import { useEffect, useRef, useState } from 'react';
import type { Language } from '../api/types';
import type { ProgramConfig, ProgramField } from '../config/types';
import { useTranslation } from '../i18n';
import {
  BooleanResponse,
  DateResponse,
  MultiResponse,
  NumberResponse,
  SelectResponse,
  TextResponse,
} from './responses';
import './ExtractionReview.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RowStatus = 'pending' | 'accepted' | 'editing' | 'saved' | 'removed';

interface RowState {
  status: RowStatus;
  /** Current confirmed value — set on accept or save. */
  value: unknown;
  /** Original OCR value — never mutated. */
  original: unknown;
  /** Working draft while editing. */
  draft: unknown;
  /** Status to restore on Cancel. */
  prevStatus: 'pending' | 'accepted' | 'saved';
}

export interface ExtractionReviewResult {
  confirmed: Record<string, unknown>;
  rejected: string[];
}

interface Props {
  extracted: Record<string, unknown>;
  config: ProgramConfig;
  loading: boolean;
  onConfirm: (result: ExtractionReviewResult) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ExtractionReview({ extracted, config, loading, onConfirm }: Props) {
  const { t, localized, lang } = useTranslation();

  // Build initial row state from extracted fields.
  const [rows, setRows] = useState<Record<string, RowState>>(() => {
    const init: Record<string, RowState> = {};
    for (const [key, value] of Object.entries(extracted)) {
      init[key] = {
        status: 'pending',
        value,
        original: value,
        draft: value,
        prevStatus: 'pending',
      };
    }
    return init;
  });

  // Track which field is currently focused for editing (for focus management).
  const editingRef = useRef<HTMLDivElement | null>(null);

  /** Immutably update a single row, asserting it exists. */
  function updateRow(key: string, patch: Partial<RowState>): (prev: Record<string, RowState>) => Record<string, RowState> {
    return (prev) => ({
      ...prev,
      [key]: { ...prev[key]!, ...patch } as RowState,
    });
  }

  // Re-focus the edit form whenever a row enters editing mode.
  useEffect(() => {
    editingRef.current?.querySelector<HTMLElement>('input, textarea, [role="radio"]')?.focus();
  });

  const fieldKeys = Object.keys(rows);

  if (fieldKeys.length === 0) {
    return null;
  }

  // ---------- status predicates ----------
  const pendingCount = fieldKeys.filter(
    (k) => rows[k]!.status === 'pending' || rows[k]!.status === 'editing',
  ).length;
  const canContinue = pendingCount === 0 && !loading;

  // ---------- row actions ----------
  function accept(key: string) {
    setRows((prev) => updateRow(key, { status: 'accepted', value: prev[key]!.original })(prev));
  }

  function startEdit(key: string) {
    setRows((prev) => {
      const row = prev[key]!;
      // prevStatus only tracks non-editing, non-removed states.
      const prev2 = (row.status === 'accepted' || row.status === 'saved' || row.status === 'pending')
        ? row.status
        : row.prevStatus;
      return updateRow(key, { status: 'editing', draft: row.value, prevStatus: prev2 })(prev);
    });
  }

  function updateDraft(key: string, value: unknown) {
    setRows(updateRow(key, { draft: value }));
  }

  function saveEdit(key: string) {
    setRows((prev) => {
      const row = prev[key]!;
      if (!isDraftValid(row.draft, config.fields[key])) return prev;
      return updateRow(key, { status: 'saved', value: row.draft })(prev);
    });
  }

  function cancelEdit(key: string) {
    setRows((prev) => updateRow(key, { status: prev[key]!.prevStatus })(prev));
  }

  function remove(key: string) {
    setRows((prev) => {
      const row = prev[key]!;
      const ps = (row.status === 'accepted' || row.status === 'saved' || row.status === 'pending')
        ? row.status
        : row.prevStatus;
      return updateRow(key, { status: 'removed', prevStatus: ps })(prev);
    });
  }

  function undoRemove(key: string) {
    setRows((prev) => updateRow(key, { status: prev[key]!.prevStatus })(prev));
  }

  function handleContinue() {
    const confirmed: Record<string, unknown> = {};
    const rejected: string[] = [];
    for (const [key, row] of Object.entries(rows)) {
      if (row.status === 'accepted' || row.status === 'saved') {
        confirmed[key] = row.value;
      } else if (row.status === 'removed') {
        rejected.push(key);
      }
    }
    onConfirm({ confirmed, rejected });
  }

  return (
    <section className="cf-extraction-review" aria-label={t('upload.review_prompt')}>
      <h2 className="cf-extraction-review__title">{t('upload.review_prompt')}</h2>

      <ul className="cf-extraction-review__list" role="list">
        {fieldKeys.map((key) => {
          const row = rows[key]!;
          const fieldDef = config.fields[key];
          const label = fieldDef ? localized(fieldDef.label) : key;
          const confirmPromptTemplate = fieldDef?.confirm_prompt
            ? localized(fieldDef.confirm_prompt)
            : undefined;
          return (
            <li key={key}>
              <ReviewRow
                fieldKey={key}
                label={label}
                confirmPromptTemplate={confirmPromptTemplate}
                fieldDef={fieldDef}
                lang={lang}
                row={row}
                editingRef={row.status === 'editing' ? editingRef : undefined}
                onAccept={() => accept(key)}
                onEdit={() => startEdit(key)}
                onDraftChange={(v) => updateDraft(key, v)}
                onSave={() => saveEdit(key)}
                onCancel={() => cancelEdit(key)}
                onRemove={() => remove(key)}
                onUndo={() => undoRemove(key)}
                t={t as TFn}
              />
            </li>
          );
        })}
      </ul>

      <div className="cf-extraction-review__footer">
        {pendingCount > 0 && (
          <p className="cf-extraction-review__pending" role="status" aria-live="polite">
            <span aria-hidden="true">○</span>{' '}
            {t('upload.verify.pending_count', { count: pendingCount })}
          </p>
        )}
        <button
          type="button"
          className="cf-btn cf-btn--primary"
          onClick={handleContinue}
          disabled={!canContinue}
          aria-disabled={!canContinue}
        >
          {loading ? t('common.loading') : t('upload.verify.continue')}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ReviewRow — one extracted field card
// ---------------------------------------------------------------------------

type TFn = (key: string, vars?: Record<string, string | number>) => string;

interface ReviewRowProps {
  fieldKey: string;
  /** Short field label — used in non-pending states and as aria-label. */
  label: string;
  /**
   * `confirm_prompt` template for the active language, e.g.
   * "Is {value} your date of birth?" — rendered only in pending state,
   * with `{value}` replaced by the display value.
   */
  confirmPromptTemplate?: string;
  fieldDef: ProgramField | undefined;
  lang: Language;
  row: RowState;
  editingRef?: React.MutableRefObject<HTMLDivElement | null>;
  onAccept: () => void;
  onEdit: () => void;
  onDraftChange: (v: unknown) => void;
  onSave: () => void;
  onCancel: () => void;
  onRemove: () => void;
  onUndo: () => void;
  t: TFn;
}

function ReviewRow({
  fieldKey,
  label,
  confirmPromptTemplate,
  fieldDef,
  lang,
  row,
  editingRef,
  onAccept,
  onEdit,
  onDraftChange,
  onSave,
  onCancel,
  onRemove,
  onUndo,
  t,
}: ReviewRowProps) {
  const canSave = isDraftValid(row.draft, fieldDef);
  const displayValue = fieldDef
    ? formatDisplayValue(row.value, fieldDef, lang)
    : String(row.value ?? '—');
  const originalDisplay = fieldDef
    ? formatDisplayValue(row.original, fieldDef, lang)
    : String(row.original ?? '—');
  const wasEdited = row.status === 'saved' && row.value !== row.original;

  // In pending state, show the full confirmation question with value interpolated.
  // In all other states, fall back to the short label.
  const headingText = row.status === 'pending' && confirmPromptTemplate
    ? confirmPromptTemplate.replace('{value}', displayValue || '—')
    : label;

  return (
    <div
      className={`cf-review-row cf-review-row--${row.status}`}
      aria-label={label}
    >
      {/* ── Header: status badge + heading + primary actions ── */}
      <div className="cf-review-row__header">
        <div className="cf-review-row__meta">
          <StatusBadge status={row.status} t={t} />
          <span className="cf-review-row__label">{headingText}</span>
        </div>

        <div className="cf-review-row__actions" role="group" aria-label={label}>
          {(row.status === 'pending') && (
            <>
              <button
                type="button"
                className="cf-btn cf-btn--sm cf-btn--primary"
                onClick={onAccept}
              >
                <span aria-hidden="true">✓</span>
                <span>{t('upload.verify.accept')}</span>
              </button>
              <button
                type="button"
                className="cf-btn cf-btn--sm cf-btn--ghost"
                onClick={onEdit}
              >
                <span aria-hidden="true">✏</span>
                <span>{t('upload.verify.edit')}</span>
              </button>
            </>
          )}

          {(row.status === 'accepted' || row.status === 'saved') && (
            <button
              type="button"
              className="cf-btn cf-btn--sm cf-btn--ghost"
              onClick={onEdit}
            >
              <span aria-hidden="true">✏</span>
              <span>{t('upload.verify.edit')}</span>
            </button>
          )}

          {row.status === 'removed' && (
            <button
              type="button"
              className="cf-btn cf-btn--sm cf-btn--ghost"
              onClick={onUndo}
            >
              <span aria-hidden="true">↩</span>
              <span>{t('upload.verify.undo')}</span>
            </button>
          )}

          {row.status === 'editing' && (
            <>
              <button
                type="button"
                className="cf-btn cf-btn--sm cf-btn--primary"
                onClick={onSave}
                disabled={!canSave}
              >
                <span aria-hidden="true">✓</span>
                <span>{t('upload.verify.save')}</span>
              </button>
              <button
                type="button"
                className="cf-btn cf-btn--sm cf-btn--ghost"
                onClick={onCancel}
              >
                {t('upload.verify.cancel')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Body: extracted value (or edit form) ── */}
      {/* In pending state the value is already in the confirm-prompt heading; skip the
          redundant value line. Show it for accepted/saved/removed so the user can see
          what was confirmed or struck through. */}
      {row.status !== 'editing' && row.status !== 'pending' && (
        <div className="cf-review-row__body">
          <div
            className={
              row.status === 'removed'
                ? 'cf-review-row__value cf-review-row__value--removed'
                : 'cf-review-row__value'
            }
            aria-hidden={row.status === 'removed'}
          >
            {displayValue || <span className="cf-review-row__empty">—</span>}
          </div>
          {wasEdited && (
            <div className="cf-review-row__original">
              <span className="cf-review-row__original-label">{t('upload.verify.original_label')}:</span>{' '}
              <span className="cf-review-row__original-value">{originalDisplay}</span>
            </div>
          )}
        </div>
      )}

      {row.status === 'editing' && fieldDef && (
        <div
          className="cf-review-row__editor"
          ref={(el) => { if (editingRef) editingRef.current = el; }}
        >
          <FieldEditor
            fieldKey={fieldKey}
            fieldDef={fieldDef}
            lang={lang}
            initialValue={row.draft}
            onChange={onDraftChange}
          />
        </div>
      )}

      {/* ── Remove affordance (visible when not already removed/editing) ── */}
      {(row.status === 'pending' || row.status === 'accepted' || row.status === 'saved') && (
        <div className="cf-review-row__secondary">
          <button
            type="button"
            className="cf-btn-link cf-btn-link--destructive"
            onClick={onRemove}
          >
            <span aria-hidden="true">✗</span>
            <span>{t('upload.verify.remove')}</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------

function StatusBadge({ status, t }: { status: RowStatus; t: TFn }) {
  if (status === 'pending') return null;

  const configs = {
    accepted: { symbol: '✓', label: t('upload.verify.status_accepted'), mod: 'accepted' },
    saved:    { symbol: '✓', label: t('upload.verify.status_edited'),   mod: 'saved'    },
    editing:  { symbol: '✏', label: t('upload.verify.status_editing'),  mod: 'editing'  },
    removed:  { symbol: '✗', label: t('upload.verify.status_removed'),  mod: 'removed'  },
  } as const;

  const cfg = configs[status as keyof typeof configs];
  if (!cfg) return null;

  return (
    <span className={`cf-review-badge cf-review-badge--${cfg.mod}`} aria-label={cfg.label}>
      <span aria-hidden="true">{cfg.symbol}</span>
      <span className="cf-review-badge__label">{cfg.label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// FieldEditor — picks the right response component for the field type
// ---------------------------------------------------------------------------

interface FieldEditorProps {
  fieldKey: string;
  fieldDef: ProgramField;
  lang: Language;
  initialValue: unknown;
  onChange: (value: unknown) => void;
}

function FieldEditor({ fieldKey, fieldDef, lang, initialValue, onChange }: FieldEditorProps) {
  const options = (fieldDef.options ?? []).map((o) => ({
    key: o.key,
    label: o.label[lang] ?? o.label.en,
  }));
  const label = fieldDef.label[lang] ?? fieldDef.label.en;
  const today = new Date().toISOString().split('T')[0];

  switch (fieldDef.type) {
    case 'text':
      return (
        <TextResponse
          key={`editor-${fieldKey}`}
          label={label}
          multiline={false}
          initialValue={typeof initialValue === 'string' ? initialValue : ''}
          autoComplete={fieldDef.input_hints?.auto_complete}
          inputMode={fieldDef.input_hints?.input_mode}
          maxLength={fieldDef.input_hints?.max_length}
          hideSubmit
          autoFocus
          onChange={onChange as (v: string) => void}
        />
      );

    case 'select':
      return (
        <SelectResponse
          key={`editor-${fieldKey}`}
          label={label}
          options={options}
          layout={fieldDef.layout}
          initialValue={typeof initialValue === 'string' ? initialValue : null}
          hideSubmit
          autoFocus
          onChange={onChange as (v: string) => void}
        />
      );

    case 'multi':
      return (
        <MultiResponse
          key={`editor-${fieldKey}`}
          label={label}
          options={options}
          initialValue={Array.isArray(initialValue) ? (initialValue as string[]) : []}
          hideSubmit
          autoFocus
          onChange={onChange as (v: string[]) => void}
        />
      );

    case 'boolean':
      // stage=true so Yes/No become selection buttons (not immediate-submit).
      // hideSubmit removes the component's own submit button — parent handles Save.
      return (
        <BooleanResponse
          key={`editor-${fieldKey}`}
          label={label}
          stage
          initialValue={typeof initialValue === 'boolean' ? initialValue : null}
          hideSubmit
          autoFocus
          onChange={onChange as (v: boolean) => void}
        />
      );

    case 'date':
      return (
        <DateResponse
          key={`editor-${fieldKey}`}
          label={label}
          initialValue={typeof initialValue === 'string' ? initialValue : ''}
          min="1900-01-01"
          max={today}
          hideSubmit
          autoFocus
          onChange={onChange as (v: string) => void}
        />
      );

    case 'number':
      return (
        <NumberResponse
          key={`editor-${fieldKey}`}
          label={label}
          initialValue={typeof initialValue === 'number' ? initialValue : null}
          min={fieldDef.min}
          max={fieldDef.max}
          step={fieldDef.step}
          prefix={fieldDef.prefix}
          suffix={fieldDef.suffix}
          hideSubmit
          autoFocus
          onChange={onChange as (v: number) => void}
        />
      );
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format an extracted value for display using option labels where available. */
function formatDisplayValue(value: unknown, fieldDef: ProgramField, lang: Language): string {
  if (value === null || value === undefined) return '';

  if (fieldDef.type === 'boolean') {
    return value === true ? 'Yes' : value === false ? 'No' : '';
  }

  if (fieldDef.type === 'select') {
    const opt = fieldDef.options?.find((o) => o.key === value);
    return opt ? (opt.label[lang] ?? opt.label.en) : String(value);
  }

  if (fieldDef.type === 'multi') {
    if (!Array.isArray(value)) return String(value);
    return value
      .map((key) => {
        const opt = fieldDef.options?.find((o) => o.key === key);
        return opt ? (opt.label[lang] ?? opt.label.en) : String(key);
      })
      .join(', ');
  }

  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(String).join(', ');
  return JSON.stringify(value);
}

/** Returns true when the draft value is non-empty and valid for the field type. */
function isDraftValid(draft: unknown, fieldDef: ProgramField | undefined): boolean {
  if (!fieldDef) return false;
  switch (fieldDef.type) {
    case 'text':    return typeof draft === 'string' && draft.trim().length > 0;
    case 'select':  return typeof draft === 'string' && draft.length > 0;
    case 'multi':   return Array.isArray(draft) && draft.length > 0;
    case 'boolean': return typeof draft === 'boolean';
    case 'date':    return typeof draft === 'string' && draft.length > 0;
    case 'number':  return typeof draft === 'number' && !isNaN(draft);
    default:        return false;
  }
}
